// src/encounter/intelligence/use-brief.ts
//
// The code-built pre-visit brief's request: POST /api/intelligence/cards/brief with { patientId, encounterId }, the
// signed-in clinician's own token, on the same rules as the Next step card (use-next-step.ts): A2 checked on arrival;
// A7/A12 one request in flight, a reply dropped unless it answers the open patient and encounter, cleared on a change;
// nothing logs. Data only: the rail draws it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStorage } from '@/auth/storage';
import { INTELLIGENCE_BASE } from '../intelligence-engine';
import { SESSION_EXPIRED, STATE_LINE } from './explanation';
import { parseBrief, type BriefV1 } from './brief';
import { withTransientRetry } from './engine-retry';

export const BRIEF_PATH = `${INTELLIGENCE_BASE}/cards/brief`;
type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface BriefView { status: 'idle' | 'loading' | 'done'; card: BriefV1 | null; notice: string | null }
const IDLE: BriefView = { status: 'idle', card: null, notice: null };

/** What a reply becomes. Pure, so the hook and its tests read one rule. */
export function briefOutcome(status: number, body: unknown): { card: BriefV1 | null; notice: string | null } {
  const card = status === 200 ? parseBrief(body) : null;
  if (card) return { card, notice: null };
  if (status === 401) return { card: null, notice: SESSION_EXPIRED };
  if (status === 429) return { card: null, notice: STATE_LINE.busy };
  if (status === 504) return { card: null, notice: STATE_LINE.timeout };
  return { card: null, notice: STATE_LINE.technical };
}

export function useBrief({ patientId, encounterId = null, fetch: fetchImpl, token }: { patientId: string | null | undefined; encounterId?: string | null; fetch?: FetchLike; token?: () => string | null }) {
  const context = `${patientId ?? ''}|${encounterId ?? ''}`;
  const [kept, setView] = useState<BriefView & { context: string }>({ ...IDLE, context });
  if (kept.context !== context) setView({ ...IDLE, context });
  const view: BriefView = kept.context === context ? kept : IDLE;
  const flight = useRef<AbortController | null>(null);

  useEffect(() => () => { flight.current?.abort(); flight.current = null; }, [patientId, encounterId]);

  const load = useCallback(async () => {
    if (!patientId || flight.current) return;
    const tok = (token ?? (() => authStorage.getAccessToken()))();
    if (!tok) { setView({ status: 'done', card: null, notice: SESSION_EXPIRED, context }); return; }
    const ac = new AbortController();
    flight.current = ac;
    setView({ ...IDLE, status: 'loading', context });
    let out: { card: BriefV1 | null; notice: string | null };
    try {
      // engine-retry.ts: a transient refusal (429, 502-504, the network) is sent again while the card keeps loading.
      const res = await withTransientRetry(
        () =>
          (fetchImpl ?? ((i: string, n: RequestInit) => fetch(i, n)))(BRIEF_PATH, {
            method: 'POST',
            headers: { authorization: `Bearer ${tok}`, 'content-type': 'application/json', accept: 'application/json' },
            body: JSON.stringify({ patientId, encounterId }),
            signal: ac.signal,
          }),
        (r) => r.status,
        ac.signal,
      );
      let body: unknown = null;
      try { body = await res.json(); } catch { body = null; }
      out = briefOutcome(res.status, body);
    } catch {
      out = { card: null, notice: STATE_LINE.technical };
    }
    if (ac.signal.aborted) return; // A7: an earlier patient's reply is never drawn.
    flight.current = null;
    setView((v) => (v.context !== context ? v : { status: 'done', ...out, context }));
  }, [patientId, encounterId, context, fetchImpl, token]);

  return { ...view, load };
}
